import Joi from '~/shared/src/utilities/joiExtended';
import {ItemAttributes} from '~/shared/src/ci/Item';
import {ReferenceAttributes} from '~/shared/src/ci/Reference';
import User from '~/shared/src/model/User';
import {Format} from '~/shared/src/utilities/format';
import {Style} from '~/shared/src/utilities/style';
import {
    configDescriptorSchema,
    FilterDescriptor,
    filterDescriptorSchema,
    SortDescriptor,
    sortDescriptorSchema,
    Align
} from '~/shared/src/validationSchemes/modules/overview';
import {
    FilterMatchMode,
    FilterValueType
} from '~/shared/src/validationSchemes/modules/overview';

export const parentItemIdSchema = Joi.number()
                                     .default( 0 )
                                     .description( 'The Id of the parent item to show referenced items for.' );

export const itemTypeIdSchema = Joi.number()
                                   .default( 0 )
                                   .description( 'The ItemTypeId to show items for.' );

export const itemTypeNameSchema = Joi.string()
                                     .empty( '' )
                                     .default( '' )
                                     .description( 'The ItemType name to show items for.' );

export const itemTypeSchema = Joi.object()
                                 .keys( {
                                     itemTypeId  : itemTypeIdSchema,
                                     itemTypeName: itemTypeNameSchema
                                 } )
                                 .oxor( 'itemTypeId', 'itemTypeName' );

export const legendSchema = Joi.boolean()
                               .default( false )
                               .description( 'Should the legend for status colors shown.' );

export type AttributeDescriptor = Exclude<keyof ItemAttributes, 'properties' | 'enumProperties'>;
const attributeDescriptorSchema = Joi.string()
                                     .valid( 'id', 'itemTypeId', 'key', 'name',
                                         'statusId',
                                         'creatorUserId', 'creationTime',
                                         'modifierUserId', 'modificationTime',
                                         'validatorUserId', 'validationTime',
                                         'validFromTime', 'validToTime' )
                                     .description( 'The attribute of an item' );

export type ParentReferenceDescriptor = Exclude<keyof ReferenceAttributes, 'referenceSetId' | 'srcItemId' | 'destItemId' | 'properties' | 'enumProperties'> | 'referenceTypeName' | 'direction';
const parentReferenceSchema = Joi.string()
                                 .valid( 'id', 'referenceTypeName', 'direction',
                                     'statusId',
                                     'modifierUserId', 'modificationTime',
                                     'validatorUserId', 'validationTime',
                                     'validFromTime', 'validToTime' )
                                 .description( 'The attribute of a reference' );

export type PropertyDescriptor = string;
const propertyDescriptorSchema = Joi.string()
                                    .description( 'The PropertyType name' );

export type ReferenceDescriptor = {
    itemType : string;
    itemTypeIs : 'dest' | 'src';
    referenceType : string;
};
const referenceDescriptorSchema = Joi.object( {
                                         itemType     : Joi.string()
                                                           .description( 'The ItemType name.' ),
                                         itemTypeIs   : Joi.string()
                                                           .valid( 'dest', 'src' )
                                                           .description( 'Is the referenced ItemType the source or the destination of the reference.' ),
                                         referenceType: Joi.string()
                                                           .description( 'The ReferenceType name.' )
                                     } )
                                     .description( 'The reference' );

export type DataDescriptor = {
    attribute : AttributeDescriptor;
    parentReference? : never;
    property? : never;
    reference? : never;
    action? : never;
} | {
    attribute? : never;
    parentReference : ParentReferenceDescriptor;
    property? : never;
    reference? : never;
    action? : never;
} | {
    attribute? : never;
    parentReference? : never;
    property : PropertyDescriptor;
    reference? : never;
    action? : never;
} | {
    attribute? : never;
    parentReference? : never;
    property? : never;
    reference : ReferenceDescriptor;
    action? : never;
} | {
    attribute? : never;
    parentReference? : never;
    property? : never;
    reference? : never;
    action : string;
};
export type OptionalDataDescriptor = {
    attribute? : AttributeDescriptor;
    parentReference? : never;
    property? : never;
    reference? : never;
    action? : never;
} | {
    attribute? : never;
    parentReference? : ParentReferenceDescriptor;
    property? : never;
    reference? : never;
    action? : never;
} | {
    attribute? : never;
    parentReference? : never;
    property? : PropertyDescriptor;
    reference? : never;
    action? : never;
} | {
    attribute? : never;
    parentReference? : never;
    property? : never;
    reference? : ReferenceDescriptor;
    action? : never;
} | {
    attribute? : never;
    parentReference? : never;
    property? : never;
    reference? : ReferenceDescriptor;
    action? : string;
};
const dataDescriptorSchema          = Joi.object()
                                         .keys( {
                                             attribute      : attributeDescriptorSchema,
                                             parentReference: parentReferenceSchema,
                                             property       : propertyDescriptorSchema,
                                             reference      : referenceDescriptorSchema
                                         } )
                                         .xor( 'attribute', 'parentReference', 'property', 'reference' )
                                         .description( 'The data descriptor for the data.' );

type ConfigDescriptor = {
    align? : Align;
    format? : Format;
    frozen? : boolean;
    hidable? : boolean;
    reorderable? : boolean;
    resizeable? : boolean;
    sortable? : boolean;
    styling? : Style[];
    width? : number;
    minWidth? : number;
    maxWidth? : number;
};

export type FinderColumnConfig = DataDescriptor & ConfigDescriptor;
export const columnsSchema = Joi.array()
                                .items(
                                    Joi.object()
                                       .concat( dataDescriptorSchema )
                                       .concat( configDescriptorSchema )
                                )
                                .default( [] )
                                .description( 'The columns the overview should contain.' );

export type FinderColumnFilter = DataDescriptor & FilterDescriptor;
export const filtersSchema = Joi.array()
                                .items(
                                    Joi.object()
                                       .concat( dataDescriptorSchema )
                                       .concat( filterDescriptorSchema )
                                       .concat( Joi.object( {
                                           value: Joi.any().description( 'The filter value for selecting items. For references the search string is used on key and name of the referenced item, for properties it is used on value of the property and for enumerated properties also for key.' )
                                       } ) )
                                )
                                .default( [] )
                                .description( 'An array of columns with filter information.' );

export type FinderColumnSorter = DataDescriptor & SortDescriptor;
export const sortersSchema = Joi.array()
                                .items(
                                    Joi.object()
                                       .concat( dataDescriptorSchema )
                                       .concat( sortDescriptorSchema )
                                )
                                .default( [] )
                                .description( 'An array of columns with sort information.' );




export type BaseColumnConfig = ConfigDescriptor & {
    field : string;
    header : string | JSX.Element;
    valueType : FilterValueType;
};

type BaseColumnDefinition = {
    panelKey : string;
    dataContext : string;
    schema : Schema;
    timezone : string;
    targetArea : string;
    parentItemId : number;
    itemTypeName : string;
    rawMode : boolean;
    showAdditionalRawNames : boolean;
    showMetaData? : boolean;
    language : string;
    filter? : boolean;
    filterFixed? : boolean;
    filterValue : string | undefined;
    filterMatchMode : FilterMatchMode;
    filterShowMatchModeSelector? : boolean;
    onFilterChange : Function;
    onFilterEnterKey : Function;
    hidden : boolean;
    tooltip? : string | false;
    user? : User;
};

export type FinderItemBaseColumnDefinition = BaseColumnConfig & {
    columnType : 'item';
    attribute : Extract<AttributeDescriptor, 'id' | 'key' | 'name' | 'itemTypeId'>;
};
export type FinderItemColumnDefinition = BaseColumnDefinition & FinderItemBaseColumnDefinition & {
    searchString : string;
};

export type FinderParentReferenceBaseColumnDefinition = BaseColumnConfig & {
    columnType : 'parentReference';
    parentReference : Extract<ParentReferenceDescriptor, 'id' | 'referenceTypeName' | 'direction'>;
};
export type FinderParentReferenceColumnDefinition = BaseColumnDefinition & FinderParentReferenceBaseColumnDefinition & {
    searchString : string;
};

export type FinderPropertyBaseColumnDefinition = BaseColumnConfig & {
    columnType : 'property';
    property : PropertyDescriptor;
    propertySetId : number;
};
export type FinderPropertyColumnDefinition = BaseColumnDefinition & FinderPropertyBaseColumnDefinition & {
    showMetaData : boolean;
    showPropertyDefaults : boolean;
};

export type FinderEnumPropertyBaseColumnDefinition = Omit<BaseColumnConfig, 'styling'> & {
    columnType : 'enumProperty',
    property : PropertyDescriptor;
    propertySetId : number;
};
export type FinderEnumPropertyColumnDefinition = BaseColumnDefinition & FinderEnumPropertyBaseColumnDefinition & {
    showMetaData : boolean;
    scrollPanelRef : any;
};

export type FinderReferenceBaseColumnDefinition = Omit<BaseColumnConfig, 'format' | 'styling'> & {
    columnType : 'reference';
    reference : ReferenceDescriptor;
    referenceSetId : number;
};
export type FinderReferenceColumnDefinition = BaseColumnDefinition & FinderReferenceBaseColumnDefinition & {
    showMetaData : boolean;
    scrollPanelRef : any;
};

export type FinderItemMetaDataBaseColumnDefinition = BaseColumnConfig & {
    columnType : 'metaData';
    attribute : Exclude<AttributeDescriptor, 'id' | 'key' | 'name' | 'itemTypeId'>;
    parentReference? : never;
};
export type FinderParentReferenceMetaDataBaseColumnDefinition = BaseColumnConfig & {
    columnType : 'metaData';
    attribute? : never;
    parentReference : Exclude<ParentReferenceDescriptor, 'id' | 'referenceSetId' | 'srcItemId' | 'destItemId'>;
};
export type FinderMetaDataBaseColumnDefinition = FinderItemMetaDataBaseColumnDefinition | FinderParentReferenceMetaDataBaseColumnDefinition;
export type FinderMetaDataColumnDefinition = BaseColumnDefinition & FinderMetaDataBaseColumnDefinition;

export type FinderActionBaseColumnDefinition = Omit<BaseColumnConfig, 'valueType'> & {
    columnType : 'action';
    action : string;
    active : boolean;
    handler : Function;
};
export type FinderActionColumnDefinition = BaseColumnDefinition & FinderActionBaseColumnDefinition;

export type FinderBaseColumnDefinition = FinderItemBaseColumnDefinition | FinderParentReferenceBaseColumnDefinition | FinderPropertyBaseColumnDefinition | FinderEnumPropertyBaseColumnDefinition | FinderReferenceBaseColumnDefinition | FinderMetaDataBaseColumnDefinition | FinderActionBaseColumnDefinition;
export type FinderCommonColumnDefinition = BaseColumnDefinition & ( FinderItemBaseColumnDefinition | FinderParentReferenceBaseColumnDefinition | FinderPropertyBaseColumnDefinition | FinderEnumPropertyBaseColumnDefinition | FinderReferenceBaseColumnDefinition | FinderMetaDataBaseColumnDefinition | FinderActionBaseColumnDefinition );
export type FinderColumnDefinition = FinderItemColumnDefinition | FinderParentReferenceColumnDefinition | FinderPropertyColumnDefinition | FinderEnumPropertyColumnDefinition | FinderReferenceColumnDefinition | FinderMetaDataColumnDefinition | FinderActionColumnDefinition;
